import React, { useState } from 'react';
import { formatEmojiText, getStatusEmoji, getImageUrl } from '../utils/helpers';
import ImageModal from './ImageModal';

function TestCaseSelector({ testCases, selectedTestCase, onSelect, onStart, results, onViewSession, planId }) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getTestCaseUsers = (testCaseId) => {
    if (!results || !results.results) return [];
    
    return results.results
      .filter(session => session.test_case_id === testCaseId)
      .map(session => ({
        username: session.username,
        platform: session.platform,
        status: session.test_case_result || 'Testing',
        started_at: session.started_at
      }))
      .sort((a, b) => new Date(b.started_at) - new Date(a.started_at)) // Most recent first
      .slice(0, 5); // Show only first 5 users
  };
  return (
    <div className="card">
      <h2>{formatEmojiText('🧪', 'Select Test Case')}</h2>
      <p>Choose which test case you want to start:</p>
      
      <div style={{ display: 'grid', gap: '1rem', marginBottom: '2rem' }}>
        {testCases.map((testCase, index) => {
          const users = getTestCaseUsers(testCase.id);
          return (
            <div 
              key={testCase.id}
              className={`step-item ${selectedTestCase === index ? 'selected' : ''}`}
              style={{ 
                cursor: 'pointer',
                border: selectedTestCase === index ? '2px solid #667eea' : `1px solid ${isDarkMode ? '#4b5563' : '#e2e8f0'}`
              }}
              onClick={() => onSelect(index)}
            >
              <div className="step-content">
                {/* Mobile: Design pill top right, Desktop: Far right */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  marginBottom: '0.5rem',
                  flexWrap: 'wrap',
                  gap: '0.5rem'
                }}>
                  <div style={{ flex: '1', minWidth: '200px' }}>
                    <h3 style={{ margin: '0 0 0.5rem 0' }}>{testCase.title}</h3>
                  </div>
                  {testCase.design_link && (
                    <div style={{ flexShrink: 0 }}>
                      <a 
                        href={testCase.design_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          padding: '0.25rem 0.75rem',
                          backgroundColor: '#8b5cf6',
                          color: 'white',
                          borderRadius: '16px',
                          textDecoration: 'none',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#7c3aed';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#8b5cf6';
                        }}
                      >
                        🎨 View Design
                      </a>
                    </div>
                  )}
                </div>

                {/* Desktop: Side-by-side layout, Mobile: Stacked */}
                <div style={{ 
                  display: 'flex', 
                  gap: '1rem',
                  marginBottom: '0.5rem',
                  flexDirection: windowWidth <= 768 ? 'column' : 'row'
                }}>
                  {/* Left side: Description and steps count */}
                  <div style={{ 
                    flex: testCase.ui_images && testCase.ui_images.length > 0 && windowWidth > 768 ? '1' : '1 1 100%',
                    minWidth: '0' // Allow flex item to shrink
                  }}>
                    <p style={{ margin: '0 0 0.5rem 0', color: '#6b7280' }}>{testCase.description}</p>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      {testCase.steps.length} step{testCase.steps.length !== 1 ? 's' : ''}
                    </div>
                  </div>

                  {/* Right side: Reference images (up to half width on desktop) */}
                  {testCase.ui_images && testCase.ui_images.length > 0 && (
                    <div style={{ 
                      flex: windowWidth > 768 ? '0 0 auto' : '1',
                      maxWidth: windowWidth > 768 ? '50%' : '100%',
                      minWidth: windowWidth > 768 ? '200px' : 'auto'
                    }}>
                      <div style={{ 
                        fontSize: '0.75rem', 
                        color: '#6b7280', 
                        marginBottom: '0.25rem',
                        textAlign: windowWidth > 768 ? 'right' : 'left'
                      }}>
                        Reference Images:
                      </div>
                      <div className="image-preview" style={{ 
                        display: 'grid', 
                        gridTemplateColumns: windowWidth > 768 
                          ? 'repeat(auto-fit, minmax(80px, 1fr))'
                          : 'repeat(auto-fit, minmax(100px, 1fr))',
                        gap: '0.25rem',
                        justifyItems: 'center'
                      }}>
                        {testCase.ui_images.map((image, imgIndex) => (
                          <img 
                            key={imgIndex} 
                            src={getImageUrl(image, planId)}
                            alt={`Test case reference ${imgIndex + 1}`}
                            style={{ 
                              cursor: 'pointer', 
                              height: windowWidth > 768 ? '60px' : '80px',
                              width: '100%',
                              objectFit: 'cover',
                              borderRadius: '4px',
                              border: `1px solid ${isDarkMode ? '#4b5563' : '#e2e8f0'}`
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedImage(image);
                              setShowImageModal(true);
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {users.length > 0 && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                      Recent testers:
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {users.map((user, userIndex) => {
                        const session = results.results.find(s => 
                          s.test_case_id === testCase.id && 
                          s.username === user.username && 
                          s.platform === user.platform
                        );
                        return (
                          <div 
                            key={userIndex}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              padding: '0.25rem 0.5rem',
                              backgroundColor: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#374151' : '#f3f4f6',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = window.matchMedia('(prefers-color-scheme: dark)').matches ? '#4b5563' : '#e5e7eb';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = window.matchMedia('(prefers-color-scheme: dark)').matches ? '#374151' : '#f3f4f6';
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (session && onViewSession) {
                                onViewSession(session);
                              }
                            }}
                            title={`Click to view ${user.username}'s test session`}
                          >
                            <span>{getStatusEmoji(user.status)}</span>
                            <span style={{ fontWeight: 'bold' }}>{user.username}</span>
                            <span style={{ color: '#6b7280' }}>({user.platform})</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {selectedTestCase !== null && (
        <div style={{ textAlign: 'center' }}>
          <button className="btn btn-primary" onClick={onStart}>
            {formatEmojiText('🚀', `Start Testing "${testCases[selectedTestCase]?.title}"`)}
          </button>
        </div>
      )}

      <ImageModal
        isOpen={showImageModal}
        onClose={() => {
          setShowImageModal(false);
          setSelectedImage(null);
        }}
        imageSrc={getImageUrl(selectedImage, planId)}
        imageAlt="Test case reference"
      />
    </div>
  );
}

export default TestCaseSelector; 